export const NUTRITION_FACT_ROWS = Object.freeze([
  { key: 'caloriesKcal', label: 'Calories', unit: 'kcal', major: true },
  { key: 'totalFatG', label: 'Total Fat', unit: 'g', major: true },
  { key: 'saturatedFatG', label: 'Saturated Fat', unit: 'g' },
  { key: 'transFatG', label: 'Trans Fat', unit: 'g' },
  { key: 'cholesterolMg', label: 'Cholesterol', unit: 'mg', major: true },
  { key: 'sodiumMg', label: 'Sodium', unit: 'mg', major: true },
  { key: 'totalCarbohydrateG', label: 'Total Carbohydrate', unit: 'g', major: true },
  { key: 'dietaryFiberG', label: 'Dietary Fiber', unit: 'g' },
  { key: 'totalSugarsG', label: 'Total Sugars', unit: 'g' },
  { key: 'addedSugarsG', label: 'Added Sugars', unit: 'g' },
  { key: 'proteinG', label: 'Protein', unit: 'g', major: true },
  { key: 'vitaminDMcg', label: 'Vitamin D', unit: 'mcg' },
  { key: 'calciumMg', label: 'Calcium', unit: 'mg' },
  { key: 'ironMg', label: 'Iron', unit: 'mg' },
  { key: 'potassiumMg', label: 'Potassium', unit: 'mg' },
] as const)

export type NutritionFactRow = (typeof NUTRITION_FACT_ROWS)[number]
export type NutritionFactKey = NutritionFactRow['key']
export type NutritionFactUnit = NutritionFactRow['unit']

export type NutritionFacts = {
  [K in NutritionFactKey]: number | null
}

const NUTRITION_FACT_KEY_SET = new Set<string>(NUTRITION_FACT_ROWS.map((row) => row.key))

export function isNutritionFactKey(value: string): value is NutritionFactKey {
  return NUTRITION_FACT_KEY_SET.has(value)
}

export function nutritionFactRowForKey(key: string): NutritionFactRow | null {
  return NUTRITION_FACT_ROWS.find((row) => row.key === key) ?? null
}

export function nullNutritionFacts(): NutritionFacts {
  return Object.fromEntries(NUTRITION_FACT_ROWS.map((row) => [row.key, null])) as NutritionFacts
}

export function zeroNutritionFacts(): NutritionFacts {
  return Object.fromEntries(NUTRITION_FACT_ROWS.map((row) => [row.key, 0])) as NutritionFacts
}

export function coerceNutritionFacts(value: unknown): NutritionFacts | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const record = value as Record<string, unknown>
  let hasKnownValue = false
  const facts = nullNutritionFacts()
  for (const row of NUTRITION_FACT_ROWS) {
    const raw = record[row.key]
    if (raw === undefined) {
      continue
    }
    if (raw === null) {
      facts[row.key] = null
      hasKnownValue = true
      continue
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      facts[row.key] = raw
      hasKnownValue = true
    }
  }
  return hasKnownValue ? facts : null
}

export function knownNutritionFactEntries(value: unknown): Array<[NutritionFactKey, number | null]> {
  const facts = coerceNutritionFacts(value)
  if (!facts) {
    return []
  }
  return NUTRITION_FACT_ROWS.map((row) => [row.key, facts[row.key]])
}

export function formatNutritionNumber(value: number): string {
  return Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export function formatNutritionFactValue(value: number | null, unit: NutritionFactUnit): string {
  return value === null ? 'Needs data' : `${formatNutritionNumber(value)} ${unit}`
}
